import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FieldWrapper, InputField, SelectFieldCountry, ConfirmDialog } from "@/components/common";
import { registerUser } from "@/services/auth-service";
import icCheck from "@/assets/icons/ic-check.svg";
import axios from "axios";

const COUNTRY_OPTIONS = ["한국", "미국", "일본", "유럽"] as const;

interface SignupFormData {
  companyName: string;
  representativeName: string;
  country: string;
  clientName: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  title: string;
  termsAgreed: boolean;
}

export const SignupPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SignupFormData>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      country: "",
      termsAgreed: false,
    },
  });

  // Watch all fields to check if they are filled
  const companyName = watch("companyName");
  const representativeName = watch("representativeName");
  const country = watch("country");
  const clientName = watch("clientName");
  const email = watch("email");
  const phone = watch("phone");
  const department = watch("department");
  const position = watch("position");
  const title = watch("title");
  const termsAgreed = watch("termsAgreed");

  // Button enabled when all required fields are filled
  const allFieldsFilled =
    !!companyName?.trim() &&
    !!representativeName?.trim() &&
    !!country?.trim() &&
    !!clientName?.trim() &&
    !!email?.trim() &&
    !!phone?.trim() &&
    !!department?.trim() &&
    !!position?.trim() &&
    !!title?.trim() &&
    termsAgreed === true;

  const [phoneDisplay, setPhoneDisplay] = useState("");

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    setPhoneDisplay(digits);
    setValue("phone", formatPhone(digits), { shouldValidate: false });
    clearErrors("phone");
  };

  const handlePhoneBlur = () => {
    const formatted = formatPhone(phoneDisplay);
    setPhoneDisplay(formatted);
  };

  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    try {
      await registerUser({
        email: data.email.trim(),
        phone: data.phone.replace(/-/g, ""),
        company_name: data.companyName.trim(),
        ceo_name: data.representativeName.trim(),
        requester_name: data.clientName.trim(),
        department: data.department.trim(),
        position: data.position.trim(),
        job_title: data.title.trim(),
        company_country: data.country.trim(),
        terms_agreed: data.termsAgreed,
      });
      setShowSuccessDialog(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error?.code;

        if (errorCode === "#M005") {
          setError("email", { type: "manual", message: "이미 등록된 이메일입니다." });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!allFieldsFilled) {
      e.preventDefault();
      return;
    }
    handleSubmit(onSubmit)(e);
  };

  return (
    <>
      {isSubmitting && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center px-5 py-10 md:px-8 md:py-[100px]"
        style={{ display: isSubmitting ? "none" : "flex" }}
      >
        <div className="w-full md:max-w-[608px] flex flex-col gap-12">
          {/* Title */}
          <div className="flex flex-col items-center justify-center gap-3">
            <h1 className="w-full text-center text-[44px] leading-[48px] md:text-[56px] md:leading-[64px] tracking-[-0.0318em] md:tracking-[-0.03em] font-normal text-black">
              Sign Up
            </h1>
            <p className="text-base leading-6 tracking-[-0.01em] text-black text-center md:text-left">
              귀하의 비즈니스를 성장시킬 준비가 되었습니다. 지금 바로 시작하세요
            </p>
          </div>

          <form onSubmit={handleFormSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-6">
              {/* Section: 회사 정보 */}
              <div className="flex flex-col gap-3 pb-6 border-b border-black/[0.15]">
                <h2 className="text-xl leading-6 tracking-[-0.018em] md:leading-7 md:tracking-[-0.02em] font-bold text-black">
                  회사 정보
                </h2>
                <div className="flex flex-col gap-6">
                  <FieldWrapper label="법인명" required error={errors.companyName?.message}>
                    <InputField
                      placeholder="법인명을 작성해주세요"
                      maxLength={100}
                      error={!!errors.companyName}
                      {...register("companyName", {
                        onChange: () => clearErrors("companyName"),
                        required: "필수 입력 항목입니다.",
                        maxLength: {
                          value: 100,
                          message: "최대 100자까지 입력 가능합니다.",
                        },
                      })}
                    />
                  </FieldWrapper>

                  <div className="flex flex-col md:flex-row gap-6">
                    <FieldWrapper
                      label="대표명"
                      required
                      error={errors.representativeName?.message}
                      className="flex-1"
                    >
                      <InputField
                        placeholder="대표명을 작성해주세요"
                        maxLength={100}
                        error={!!errors.representativeName}
                        {...register("representativeName", {
                          onChange: () => clearErrors("representativeName"),
                          maxLength: {
                            value: 100,
                            message: "최대 100자까지 입력 가능합니다.",
                          },
                        })}
                      />
                    </FieldWrapper>
                    <FieldWrapper
                      label="기업 소재국"
                      required
                      error={errors.country?.message}
                      className="flex-1"
                    >
                      <SelectFieldCountry
                        options={COUNTRY_OPTIONS}
                        value={watch("country")}
                        placeholder="소재국을 선택해주세요"
                        allowCustom
                        customMaxLength={50}
                        onChange={(val: string) => {
                          setValue("country", val, { shouldValidate: false });
                          clearErrors("country");
                        }}
                      />
                      <input
                        type="hidden"
                        {...register("country", {
                          maxLength: {
                            value: 50,
                            message: "최대 50자까지 입력 가능합니다.",
                          },
                        })}
                      />
                    </FieldWrapper>
                  </div>
                </div>
              </div>

              {/* Section: 계정 설정 */}
              <div className="flex flex-col gap-3">
                <h2 className="text-xl leading-6 tracking-[-0.018em] md:leading-7 md:tracking-[-0.02em] font-bold text-black">
                  계정 설정
                </h2>
                <div className="flex flex-col gap-6">
                  <FieldWrapper label="의뢰자명" required error={errors.clientName?.message}>
                    <InputField
                      placeholder="의뢰자명을 작성해주세요"
                      maxLength={50}
                      error={!!errors.clientName}
                      {...register("clientName", {
                        onChange: () => clearErrors("clientName"),
                        required: "필수 입력 항목입니다.",
                        maxLength: {
                          value: 50,
                          message: "최대 50자까지 입력 가능합니다.",
                        },
                      })}
                    />
                  </FieldWrapper>

                  <div className="flex flex-col md:flex-row gap-6">
                    <FieldWrapper
                      label="이메일"
                      required
                      error={errors.email?.message}
                      className="flex-1"
                    >
                      <InputField
                        placeholder="이메일을 입력해 주세요"
                        error={!!errors.email}
                        {...register("email", {
                          onChange: () => clearErrors("email"),
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
                            message: "유효하지 않은 형식입니다.",
                          },
                        })}
                      />
                    </FieldWrapper>
                    <FieldWrapper
                      label="전화번호"
                      required
                      error={errors.phone?.message}
                      className="flex-1"
                    >
                      <InputField
                        placeholder="010-1234-5678"
                        value={phoneDisplay}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        error={!!errors.phone}
                      />
                      <input
                        type="hidden"
                        {...register("phone", {
                          validate: (val) => {
                            if (!val) return true;
                            const digits = (val || "").replace(/-/g, "");
                            if (!/^010\d{7,8}$/.test(digits))
                              return "유효하지 않은 전화번호입니다.";
                            return true;
                          },
                        })}
                      />
                    </FieldWrapper>
                  </div>

                  <div className="flex flex-col md:flex-row gap-6">
                    <FieldWrapper
                      label="부서"
                      required
                      error={errors.department?.message}
                      className="flex-1"
                    >
                      <InputField
                        placeholder="부서 입력해 주세요"
                        maxLength={50}
                        error={!!errors.department}
                        {...register("department", {
                          onChange: () => clearErrors("department"),
                          maxLength: {
                            value: 50,
                            message: "최대 50자까지 입력 가능합니다.",
                          },
                        })}
                      />
                    </FieldWrapper>
                    <FieldWrapper
                      label="직급"
                      required
                      error={errors.position?.message}
                      className="flex-1"
                    >
                      <InputField
                        placeholder="직급 입력해 주세요"
                        maxLength={50}
                        error={!!errors.position}
                        {...register("position", {
                          onChange: () => clearErrors("position"),
                          maxLength: {
                            value: 50,
                            message: "최대 50자까지 입력 가능합니다.",
                          },
                        })}
                      />
                    </FieldWrapper>
                    <FieldWrapper
                      label="직책"
                      required
                      error={errors.title?.message}
                      className="flex-1"
                    >
                      <InputField
                        placeholder="직책 입력해 주세요"
                        maxLength={50}
                        error={!!errors.title}
                        {...register("title", {
                          onChange: () => clearErrors("title"),
                          maxLength: {
                            value: 50,
                            message: "최대 50자까지 입력 가능합니다.",
                          },
                        })}
                      />
                    </FieldWrapper>
                  </div>

                  {/* Terms checkbox */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register("termsAgreed", {
                          onChange: () => clearErrors("termsAgreed"),
                        })}
                        className="w-5 h-5 rounded border border-[#00000027] accent-dark"
                      />
                      <span className="text-base leading-6 tracking-[-0.01em] text-content-strong">
                        <Link to="/terms" className="font-bold text-black underline">
                          서비스 이용약관
                        </Link>
                        {" 및 "}
                        <Link to="/privacy" className="font-bold  text-black underline">
                          개인정보 수집-이용약관
                        </Link>
                        {" 동의하기 "}
                        <span className="text-[#CB3A31]">*</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit + Login link */}
            <div className="flex flex-col items-center gap-4">
              <button
                type="submit"
                disabled={!allFieldsFilled || isSubmitting}
                className="h-12 w-full bg-[#FFAA72] text-black text-base leading-6 tracking-[-0.01em] font-bold disabled:text-[#B0B0B0] rounded-[4px]  disabled:bg-[#E0E0E0] disabled:cursor-not-allowed hover:bg-dark/90 transition-colors"
              >
                회원가입 요청
              </button>
              <div className="flex items-center gap-2">
                <span className="text-base leading-6 tracking-[-0.01em] text-black">
                  이미 회원이신가요?
                </span>
                <Link
                  to="/login"
                  className="text-base leading-6 tracking-[-0.01em] font-bold text-black underline "
                >
                  로그인하기
                </Link>
              </div>
            </div>
          </form>
        </div>

        <ConfirmDialog
          open={showSuccessDialog}
          onOpenChange={setShowSuccessDialog}
          iconVariant="success"
          icon={<img src={icCheck} alt="" />}
          title="회원가입 신청 완료"
          description="계정 신청이 완료되었어요. 승인이 끝나면 바로 로그인할 수 있어요!"
          hideClose
          primaryAction={{
            label: "OK",
            onClick: () => navigate("/login"),
          }}
        />
      </div>
    </>
  );
};

/* ─── Sub-components ─── */
